import clsx from 'clsx';
import { type PropsWithChildren } from 'react';

import { useNavigateSpinButton } from './hooks/useNavigateSpinButton.js';
import { useValueOverflow } from './hooks/useValueOverflow.js';
import { SpinButtonGroupSeparator } from './SpinButtonGroupSeparator.js';
import type { SpinButtonGroupProps } from './types.js';
import { NumberSpinButton } from '../number-spin-button/NumberSpinButton.js';
import { spinButtonGroupCSS } from '../SpinButton.sty.js';
import { TextSpinButton } from '../text-spin-button/TextSpinButton.js';

const InternalSpinButtonGroup = ({
  children,
  valueOverflow = false,
  className: consumerClassName,
  style: consumerStyle,
  ...remainingProps
}: PropsWithChildren<SpinButtonGroupProps>) => {
  const { clonedChildren, spinButtonRefs, triggerRefs } = useValueOverflow(
    children,
    valueOverflow,
  );

  const { onKeyDownHandler } = useNavigateSpinButton(
    spinButtonRefs,
    triggerRefs,
  );

  return (
    <span
      className={clsx(spinButtonGroupCSS, consumerClassName)}
      style={consumerStyle}
      onKeyDown={onKeyDownHandler}
      {...remainingProps}
    >
      {clonedChildren}
    </span>
  );
};

/**
 * Use the _SpinButtonGroup to combine text and number spinbuttons and provide ootb navigation between them.
 * @internal
 */
export const SpinButtonGroup = Object.assign(InternalSpinButtonGroup, {
  Number: NumberSpinButton,
  Text: TextSpinButton,
  Separator: SpinButtonGroupSeparator,
});

(
  SpinButtonGroup as typeof SpinButtonGroup & { displayName: string }
).displayName = '_SpinButtonGroup';
